import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useState, useRef, useMemo } from 'react';
import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { adDisplayName } from '@/lib/ad-display-name';
import { resolveActiveFunnelTab, visibleFunnelStages } from '@/lib/ad-funnel-tab';
import { campaignDataAtom, campaignTaskTrackingAtom, selectedAdIdAtom, rebuildingAdIdAtom, showAdPreviewModalAtom, selectedAdTabAtom, googleAdsAccountAtom, organizationDataAtom, newlyCreatedAdIdAtom } from '@/store';
import { canResumeInGlitch, isAwaitingFirstPublish } from '@/lib/campaign-publish-state';
import { CAMPAIGN_STATE } from '@/lib/types';
import { dedupeKey, hasUnfallbackedTag } from '@/lib/ad-copy-tokens';
import { extractApiErrorDetail } from '@/lib/api-error';
import { CampaignSidebar } from '@/components/campaign/campaign-sidebar';
import { CampaignSidebarToolbar } from '@/components/campaign/campaign-sidebar-toolbar';
import { KeywordManager } from '@/components/campaign/ads/keyword-manager';
import { NegativeKeywordsManager } from '@/components/campaign/ads/negative-keywords-manager';
import { HeadlineManager } from '@/components/campaign/ads/headline-management';
import { DescriptionManagement } from '@/components/campaign/ads/description-management';
import { AdFailedBanner } from '@/components/campaign/ad-failed-banner';
import { CampaignStatusMessages } from '@/components/campaign/campaign-status-messages';
import { AdPreviewModal } from '@/components/campaign/ads/ad-preview-modal';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { StringBadge } from '@/components/glitch-ui/string-badge';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TabCardPanel, tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  Plus,
  ChevronDown,
  Trash,
  Trash2,
  Eye,
  AlertCircle,
  Pause,
  Play,
  PauseCircle,
  ArrowRight,
  ExternalLink,
  Check,
  Tag,
  Wand2,
  X,
} from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/glitch-ui/data-table';
import { LabelCombobox } from '@/components/glitch-ui/label-combobox';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';

import {
  campaignQueryOptions,
  campaignTaskTrackingQueryOptions,
  campaignHeadlineMetricsQueryOptions,
  campaignDescriptionMetricsQueryOptions,
  useUpdateAdMutationOptions,
  useRenameAdGroupMutationOptions,
  useAdRestartMutation,
  accountLabelsQueryOptions,
  availableCompetitorsQueryOptions,
  useAdNegativeKeywordsQuery,
} from '@/lib/query-options';
import { pauseAd, resumeAd, deleteAd, generateAllHeadlines, generateAllDescriptions, pauseFunnelStage, resumeFunnelStage, createAd } from '@/lib/apis';
import { AdCreateModal } from '@/components/campaign/ads/ad-create-modal';
import { useToast } from '@/hooks/useToast';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useRefetchOnTasksSettled } from '@/hooks/useRefetchOnTasksSettled';
import { useRefreshCampaignOnView } from '@/hooks/useRefreshCampaignOnView';
import { useCampaignActions } from '@/hooks/useCampaignActions';
import { useGoLive } from '@/hooks/useGoLive';
import { useDeployChanges } from '@/hooks/useDeployChanges';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useInvalidateQuery } from '@/hooks/useInvalidateQuery';
import { usePostHog } from 'posthog-js/react';
import useAreAllTasksCompleted from '@/hooks/useAreAllTasksCompleted';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';
import { getFunnelStageLabel, isBrandCampaign, isCampaignType, isTaskTrackingSummary, isValidUrl } from '@/lib/utils';
import { isSameHostOrGeneratedLp } from '@/lib/landing-page-host';
import { AD_STATE, CAMPAIGN_TYPE, FunnelStage, type Ad, type AdFunnelStage, type AdLabel, type Keyword, type AlternativeKeyword, type Headline, type Description, type CampaignData } from '@/lib/types';

function AdPageWrapper() {
  const { campaignslug, adId } = Route.useParams();
  return <AdPage key={`${campaignslug}-${adId}`} />;
}

export const Route = createFileRoute('/_authenticated/_layout/campaigns/google/$campaignslug/_slug/ad/$adId')({
  component: AdPageWrapper,
});

export type AdSection = 'overview' | 'keywords' | 'negatives' | 'headlines' | 'descriptions' | 'labels';

// #5548: an Ad's landing page URL must stay on the same domain as the campaign (the backend
// enforces this too). #6480: a GlitchAds-generated landing-page host (e.g. pages.glitchads.ai) is
// always allowed, since its copy is written for that page regardless of the campaign's domain.
const isSameHost = (a: string, b: string): boolean => {
  // No reference URL to compare against (e.g. a draft ad that never had a landing page) means there is
  // no same-domain constraint to enforce, so any well-formed new URL is allowed.
  if (!b?.trim()) return true;
  return isSameHostOrGeneratedLp(a, b);
};

// #5548: warning shown before an Ad's landing page URL is changed (headlines are generated per
// landing page, so changing it decouples them from the page they were written for).
const AD_URL_WARNING =
  'Headlines are generated per landing page based on the content of the landing page. Changing this may result in poor pagerank, increasing the cost of your ads for this campaign.';

function AdLabelsSection({
  labels,
  accountLabels,
  isReadOnly,
  isGenerating = false,
  onAdd,
  onRemove,
}: {
  labels: AdLabel[];
  accountLabels: AdLabel[];
  isReadOnly: boolean;
  isGenerating?: boolean;
  onAdd: (label: AdLabel) => void;
  onRemove: (labelId: string) => void;
}) {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const availableLabels = accountLabels.filter(
    (a) => !labels.some((c) => c.name.toLowerCase() === a.name.toLowerCase()),
  );

  const handleAdd = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = accountLabels.find((l) => l.name.toLowerCase() === trimmed.toLowerCase());
    onAdd(existing ?? { id: '', name: trimmed, resource_name: '' });
  };

  const handleRemove = (labelId: string) => {
    onRemove(labelId);
  };

  const columns = useMemo<ColumnDef<AdLabel, unknown>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span className="text-sm text-gray-900">{row.original.name}</span>,
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => {
        if (isReadOnly) return null;
        return (
          <div className="flex justify-end">
            <button
              onClick={() => handleRemove(row.original.id || row.original.name)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
              aria-label={`Remove label ${row.original.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ], [isReadOnly, labels]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Labels</h2>
            <StringBadge string={String(labels.length)} size="sm" />
          </div>
          <p className="text-sm text-gray-600 mt-1">Labels to organise and filter your ads.</p>
        </div>
        {!isReadOnly && !isGenerating && (
          <Button variant="outline" className="text-gray-900" onClick={() => setIsAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Label
          </Button>
        )}
      </div>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Add Label</DialogTitle>
          <p className="text-sm text-gray-600">Search existing labels or create a new one.</p>

          {labels.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {labels.map((l) => (
                <span
                  key={l.id || l.name}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-900"
                >
                  <Tag className="h-3 w-3 text-gray-500" />
                  {l.name}
                  {!isReadOnly && (
                    <button
                      onClick={() => handleRemove(l.id || l.name)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label={`Remove label ${l.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          <LabelCombobox
            options={availableLabels}
            onAdd={handleAdd}
          />

          <div className="flex justify-end">
            <Button variant="outline" className="text-gray-900" onClick={() => setIsAddOpen(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DataTable<AdLabel>
        data={labels}
        columns={columns}
        pageSize={999}
        emptyIcon={Tag}
        emptyTitle="No Labels"
        emptyDescription="Labels to organise and filter your ads."
      />
    </div>
  );
}

// Loading skeleton
function AdPageSkeleton() {
  return (
    <div className='flex flex-col h-full'>
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-3">
            <div className="flex items-end justify-between">
              <Skeleton className="h-10 w-32 rounded-t-lg" />
              <Skeleton className="h-9 w-20 mb-1" />
            </div>
            <Card className="rounded-lg rounded-tl-none border bg-white">
              <CardContent className="p-2">
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="p-2">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-9">
            <div className="flex items-end justify-between">
              <div className="flex items-end gap-1">
                <Skeleton className="h-10 w-24 rounded-t-lg" />
                <Skeleton className="h-10 w-20 rounded-t-lg" />
                <Skeleton className="h-10 w-20 rounded-t-lg" />
                <Skeleton className="h-10 w-28 rounded-t-lg" />
              </div>
              <Skeleton className="h-9 w-20 mb-1" />
            </div>
            <Card className="rounded-lg rounded-tl-none border bg-white">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdPage() {
  const campaign = useAtomValue(campaignDataAtom);
  const { adId } = Route.useParams();
  const location = useLocation();
  const setSelectedAdId = useSetAtom(selectedAdIdAtom);
  const [persistedAdTab, setPersistedAdTab] = useAtom(selectedAdTabAtom);

  // Set the selected ad from URL param on mount
  useEffect(() => {
    const numericAdId = parseInt(adId, 10);
    if (!isNaN(numericAdId)) {
      setSelectedAdId(numericAdId);
    }
  }, [adId, setSelectedAdId]);

  if (!campaign) {
    return <AdPageSkeleton />;
  }

  // Parse section from hash, fall back to persisted atom
  const hash = location.hash?.replace('#', '') || '';
  const AD_SECTIONS: AdSection[] = ['overview', 'headlines', 'descriptions', 'keywords', 'negatives', 'labels'];
  let section: AdSection = 'overview';
  if (hash && AD_SECTIONS.includes(hash as AdSection)) {
    section = hash as AdSection;
  } else if (AD_SECTIONS.includes(persistedAdTab as AdSection)) {
    section = persistedAdTab as AdSection;
  }

  return <AdPageContent campaign={campaign} section={section} setPersistedAdTab={setPersistedAdTab} />;
}

function AdPageContent({ campaign, section, setPersistedAdTab }: { campaign: CampaignData; section: AdSection; setPersistedAdTab: (tab: string) => void }) {
  const navigate = useNavigate();
  const taskTrackingFromAtom = useAtomValue(campaignTaskTrackingAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const invalidateQuery = useInvalidateQuery();
  const posthog = usePostHog();
  const { areCompleted: areAllTasksCompleted } = useAreAllTasksCompleted();
  const { actions: campaignActions, states: campaignStates } = useCampaignActions({ campaign });
  // Status slot busy (spinner or confirmation showing) — holds back the Resume button (#5669).
  const [statusBusy, setStatusBusy] = useState(false);
  // Read-only check for external/cross-org/cross-account campaigns
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const isExternal = campaign?.origin === 'external';
  // A managed external campaign is writable like a native one (#6381 chunk D/G);
  // mirrors the campaign detail page's gating.
  const isManagedExternal = isExternal && !!campaign?.managed_at;
  const isCrossOrg = !!(organizationData?.id && campaign?.organization_id && campaign.organization_id !== organizationData.id);
  const isCrossAccount = !isCrossOrg && !!(googleAdsAccount?.googleads_account_id && campaign?.googleads_account?.googleads_account_id && campaign.googleads_account.googleads_account_id !== googleAdsAccount.googleads_account_id);
  const isArchived = campaign?.is_deleted === true;
  // Failed-generation campaigns are read-only on this surface too — same precedence as the campaign detail page.
  // See #4413. Reuses campaignTaskTracking already fetched below; this derivation runs after the data lands.
  const { data: campaignTaskTracking } = useQuery(campaignTaskTrackingQueryOptions(campaign.id, 'AdCreation'));
  const hasGenerationFailed = (() => {
    const cc = campaignTaskTracking?.campaign_creation;
    if (!cc) return false;
    const failed = cc.failed ?? 0;
    const stuck = (cc as { stuck_in_progress?: number }).stuck_in_progress ?? 0;
    const inProgress = cc.in_progress ?? 0;
    const restartAttempts = (cc as { restart_attempt_count?: number }).restart_attempt_count ?? 0;
    const isActivelyRetrying = inProgress > 0 && restartAttempts > 0;
    return (failed > 0 || stuck > 0) && !isActivelyRetrying;
  })();
  const isReadOnly = (isExternal && !isManagedExternal) || isCrossOrg || isCrossAccount || isArchived || hasGenerationFailed;

  // Headline & description metrics

  // Selected ad state
  const [selectedAdId, setSelectedAdId] = useAtom(selectedAdIdAtom);
  const setRebuildingAdId = useSetAtom(rebuildingAdIdAtom);
  const [newlyCreatedAdId, setNewlyCreatedAdId] = useAtom(newlyCreatedAdIdAtom);
  const selectedAd = campaign.ads?.find((ad: Ad) => ad.id === selectedAdId) as Ad | undefined;
  // Inherited (read-only) negatives applied to this ad group via the campaign and account shared
  // lists — surfaced alongside the ad's own negatives so the Negatives tab shows everything.
  const { data: adNegativeData } = useAdNegativeKeywordsQuery(campaign.id, selectedAdId ?? 0);
  // Retain last non-null ad ID so onSave works even if the atom is cleared before the blocker fires
  const stableAdIdRef = useRef<number | null>(selectedAdId);
  if (selectedAdId !== null) stableAdIdRef.current = selectedAdId;

  // Account-level label catalog — used by the Labels tab picker.
  const { data: accountLabelsData } = useQuery(accountLabelsQueryOptions());

  // Modal states
  const [showDeleteAdConfirm, setShowDeleteAdConfirm] = useState(false);
  const [isCreateAdModalOpen, setIsCreateAdModalOpen] = useState(false);
  const [prefixedUrl, setPrefixedUrl] = useState(campaign.website_url ?? '');
  // Competitor campaigns add an ad per competitor, not per landing page (#6252).
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(null);
  const isCompetitorCampaign = isCampaignType(campaign, CAMPAIGN_TYPE.COMPETITOR);
  const { data: availableCompetitors, isLoading: isLoadingCompetitors } = useQuery(
    availableCompetitorsQueryOptions(campaign.id, isCompetitorCampaign && isCreateAdModalOpen),
  );
  const [showAdPreviewModal, setShowAdPreviewModal] = useAtom(showAdPreviewModalAtom);

  // Campaign filter state (persisted via user settings)
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters: updateCampaignFilters } = useCampaignSidebarFilters();

  // Ad state management
  const [adStates, setAdStates] = useState<
    Record<
      string,
      {
        keywords: Keyword[];
        headlines: Headline[];
        descriptions: Description[];
        alternativeKeywords: AlternativeKeyword[];
        negativeKeywords: any[];
        labels: AdLabel[];
        customKeyword: string;
        name: string;
        url: string;
        isPaused: boolean;
      }
    >
  >({});

  const [adDirtyStates, setAdDirtyStates] = useState<
    Record<
      string,
      {
        isKeywordsDirty: boolean;
        isHeadlinesDirty: boolean;
        isDescriptionsDirty: boolean;
        isAlternativeKeywordsDirty: boolean;
        isNegativeKeywordsDirty: boolean;
        isLabelsDirty: boolean;
        isNameDirty: boolean;
        isUrlDirty: boolean;
        isPausedDirty: boolean;
        // True when local state differs from server in any field. Gates the
        // refetch-rehydrate effect's "preserve in-progress edits" guard.
        isAdDirty: boolean;
        // Drives visibility of the top-bar Save Changes button. Currently mirrors
        // isAdDirty: every persisted field — including headlines and descriptions —
        // requires the manual Save Changes button. Nothing auto-saves. See #4548.
        requiresManualSave: boolean;
      }
    >
  >({});

  const adDirtyStatesRef = useRef(adDirtyStates);
  adDirtyStatesRef.current = adDirtyStates;
  const isAnyAdDirty = Object.values(adDirtyStates).some((s) => s.isAdDirty);

  // Brief "Saved ✓" confirmation shown next to the ad top-bar Save Changes
  // button after a successful save. Auto-clears after 2s. Matches the
  // reference pattern in business/budget-v2.tsx + organizations/index.tsx (#4387).
  const [adSaveSuccess, setAdSaveSuccess] = useState(false);
  // Local saving flag so the spinner stays continuous across the whole
  // handleSaveAd flow — mutation pending alone briefly flips false between
  // the mutateAsync and the refetch + state reconciliation that follows,
  // which would otherwise show spinner → text → Saved ✓ on save. #4387.
  const [isAdSavingLocal, setIsAdSavingLocal] = useState(false);

  const [activeFunnelTab, setActiveFunnelTab] = useState<FunnelStage>(FunnelStage.LOWER_FUNNEL);

  // #6612: imported campaigns carry angle stages (angle_1, angle_2, ...), never the native funnel
  // stage this starts on, and every headline and description consumer below filters on
  // `ad_funnel_stage.funnel === activeFunnelTab`. Without this the page opened on a stage the ad
  // does not have and rendered nothing over copy that was fully present. Re-runs per selected ad,
  // so switching ads inside a campaign is covered, and keeps a tab the newly opened ad also has.
  useEffect(() => {
    setActiveFunnelTab((current) => resolveActiveFunnelTab(selectedAd?.ad_funnel_stage, current));
  }, [selectedAdId, selectedAd?.ad_funnel_stage]);

  const [isGeneratingHeadlines, setIsGeneratingHeadlines] = useState(false);
  const [newlyGeneratedHeadlineIds, setNewlyGeneratedHeadlineIds] = useState<Set<number>>(new Set());
  const [isGeneratingDescriptions, setIsGeneratingDescriptions] = useState(false);
  const [showHeadlineAddModal, setShowHeadlineAddModal] = useState(false);
  const [showDescriptionAddModal, setShowDescriptionAddModal] = useState(false);
  const [duplicateHeadlineTexts, setDuplicateHeadlineTexts] = useState<Set<string>>(new Set());
  // #5548: ad id whose landing-page URL change is awaiting confirmation (null = modal closed).
  const [urlWarningAdId, setUrlWarningAdId] = useState<number | null>(null);

  // Task tracking — moved above isReadOnly so hasGenerationFailed can participate (see above).

  // Headline & description metrics
  const isLive = campaign.state === CAMPAIGN_STATE.LIVE;
  const { data: headlineMetricsData, isLoading: headlineMetricsLoading } = useQuery({
    ...campaignHeadlineMetricsQueryOptions(campaign.id),
    enabled: isLive,
  });
  const { data: descriptionMetricsData, isLoading: descriptionMetricsLoading } = useQuery({
    ...campaignDescriptionMetricsQueryOptions(campaign.id),
    enabled: isLive,
  });

  // Mutations
  const updateAdMutation = useUpdateAdMutationOptions();
  const renameAdGroupMutation = useRenameAdGroupMutationOptions();

  // #5924: inline edit of the Google Ads ad group name. Keyed by ad id so the pending
  // edit follows the selected ad; cleared once persisted (the campaign refetch supplies
  // the saved value). This is a sync-to-Google action, kept separate from the ad's
  // local Save Changes dirty-state flow.
  const [adGroupNameEdits, setAdGroupNameEdits] = useState<Record<number, string>>({});
  const [adGroupNameErrors, setAdGroupNameErrors] = useState<Record<number, string>>({});

  // The prompt's Save runs handleSaveAd, which rejects an ad below the Google Ads RSA
  // minimums. Surface that as a block reason so leaving an under-minimum ad offers a clean
  // Discard instead of a Save that throws (#5598).
  // Rows staged for deletion (is_deleted) stay in adState so the save payload can tell
  // the backend exactly which rows to pause — the backend never pauses rows merely
  // absent from the payload. They must not count toward limits, duplicates, or diffs.
  const activeRows = <T extends { is_deleted?: boolean }>(rows: T[]): T[] => rows.filter((r) => !r.is_deleted);

  const selectedSaveState = selectedAdId ? adStates[selectedAdId] : null;
  const activeSaveHeadlineCount = selectedSaveState ? activeRows(selectedSaveState.headlines).length : 0;
  const activeSaveDescriptionCount = selectedSaveState ? activeRows(selectedSaveState.descriptions).length : 0;
  const adSaveBlockedReason = selectedSaveState
    ? activeSaveHeadlineCount < 3
      ? `Ads need at least 3 headlines to publish to Google Ads (this ad has ${activeSaveHeadlineCount}).`
      : activeSaveDescriptionCount < 2
        ? `Ads need at least 2 descriptions to publish to Google Ads (this ad has ${activeSaveDescriptionCount}).`
        : null
    : null;

  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty: isAnyAdDirty,
    // onSave goes through requestAdSave so a dirty landing-page URL still hits the confirm (#5548).
    onSave: () => requestAdSave(stableAdIdRef.current!),
    // Current ad only. Switching to a DIFFERENT ad must prompt: the ad route remounts per
    // id (#4519), so an un-prompted switch silently discards the unsaved edit. #5598.
    allowedBasePath: `/campaigns/google/${campaign.slug}/ad/${stableAdIdRef.current}`,
    isSaving: isAdSavingLocal || updateAdMutation.isPending,
    saveBlockedReason: adSaveBlockedReason,
  });
  // Save UX is contained to the Save Changes button itself (spinner during
  // save, "Saved ✓" sibling on success). We deliberately do NOT throw up a
  // full-page dark overlay during the mutation — by the time the overlay
  // clears the user has looked away from the surface where the "Saved" tick
  // would appear. #4387.


  const pauseFunnelStageMutation = useMutation({
    mutationFn: (funnelStageId: number) => pauseFunnelStage(campaign.id, selectedAdId!, funnelStageId),
    onSuccess: (_, funnelStageId) => {
      // Optimistically update the local ad data before refetch
      if (selectedAd?.ad_funnel_stage) {
        selectedAd.ad_funnel_stage = selectedAd.ad_funnel_stage.map((fs: AdFunnelStage) =>
          fs.id === funnelStageId ? { ...fs, is_paused: true } : fs
        );
      }
      refetchCampaign();
    },
    onError: () => {
      toast({ description: 'Failed to pause funnel stage', variant: 'destructive' });
    },
  });

  const resumeFunnelStageMutation = useMutation({
    mutationFn: (funnelStageId: number) => resumeFunnelStage(campaign.id, selectedAdId!, funnelStageId),
    onSuccess: (_, funnelStageId) => {
      if (selectedAd?.ad_funnel_stage) {
        selectedAd.ad_funnel_stage = selectedAd.ad_funnel_stage.map((fs: AdFunnelStage) =>
          fs.id === funnelStageId ? { ...fs, is_paused: false } : fs
        );
      }
      refetchCampaign();
    },
    onError: () => {
      toast({ description: 'Failed to resume funnel stage', variant: 'destructive' });
    },
  });

  const handleToggleFunnelStagePause = (funnelStageId: number, isPaused: boolean) => {
    posthog?.capture('funnel_stage_pause_toggled', { campaign_id: campaign?.id, ad_id: selectedAdId, is_paused: !isPaused });
    if (isPaused) {
      resumeFunnelStageMutation.mutate(funnelStageId);
    } else {
      pauseFunnelStageMutation.mutate(funnelStageId);
    }
  };

  const getActiveFunnelStage = (): AdFunnelStage | undefined => {
    return selectedAd?.ad_funnel_stage?.find(
      (fs: AdFunnelStage) => fs.funnel === activeFunnelTab
    );
  };

  const deleteAdMutation = useMutation({
    mutationFn: () => deleteAd(campaign.id, selectedAdId!),
    onSuccess: async () => {
      // Capture before async — campaign prop is stale after refetch
      const deletedAdId = selectedAdId;
      const remainingAds = campaign.ads?.filter((ad: Ad) => ad.id !== deletedAdId) || [];
      const nextAd = remainingAds[0];

      await refetchCampaign();

      if (nextAd) {
        setSelectedAdId(nextAd.id);
        navigate({ to: `/campaigns/google/${campaign.slug}/ad/${nextAd.id}` });
      } else {
        setSelectedAdId(null);
        navigate({ to: `/campaigns/google/${campaign.slug}` });
      }
      setShowDeleteAdConfirm(false);
    },
    onError: () => {
      toast({ description: 'Failed to delete ad', variant: 'destructive' });
    },
  });

  // Ad restart mutation
  const { mutate: restartAd, isPending: isRestartingAd } = useAdRestartMutation(campaign.id, selectedAdId || 0);

  // Computed values
  const isBrand = isBrandCampaign(campaign);
  const { isLoading: isTasksLoading, areCompleted: areTasksCompleted } = useAreAllTasksCompleted();
  const isStillGenerating = isTasksLoading || !areTasksCompleted;
  const { goLive, isGoingLive, blockingReason, PaymentGate: GoLivePaymentGate } = useGoLive(campaign);
  // Deploy pending changes to Google Ads — same hook/trigger as the campaign page (#5583),
  // so a new ad added here can be deployed without navigating back to the campaign.
  const { hasPendingChanges, deploy: handleDeployChanges, deployDisabled, overBudgetTitle, isDeploying, isForeignTransitionPending } = useDeployChanges(campaign, isStillGenerating);


  const { sumTotal, taskProcessing } = Object.values(campaignTaskTracking || {}).reduce(
    (acc, value) => {
      if (isTaskTrackingSummary(value)) {
        acc.sumTotal += value.total;
        acc.taskProcessing += value.in_progress;
      }
      return acc;
    },
    { sumTotal: 0, taskProcessing: 0 },
  );

  // Initialize state for each ad, preserving dirty (unsaved) edits on refetch
  useEffect(() => {
    setAdStates((prev) => {
      const newStates: Record<string, any> = {};
      campaign.ads.forEach((ad: Ad) => {
        const isDirty = adDirtyStatesRef.current[ad.id]?.isAdDirty;
        if (prev[ad.id] && isDirty) {
          // Preserve user's in-progress edits
          newStates[ad.id] = prev[ad.id];
        } else {
          newStates[ad.id] = {
            keywords: [...(ad.keywords || []), ...(ad.paused_keywords || [])],
            headlines: ad.headlines || [],
            descriptions: ad.descriptions || [],
            alternativeKeywords: ad.alternative_keywords || [],
            negativeKeywords: ad.negative_keywords || [],
            labels: ad.labels || [],
            customKeyword: prev[ad.id]?.customKeyword || '',
            name: adDisplayName(ad, (campaign.ads?.findIndex((a: Ad) => a.id === ad.id) ?? 0) + 1),
            url: ad.url || '',
            isPaused: ad.is_paused,
          };
        }
      });
      return newStates;
    });
    setAdDirtyStates((prev) => {
      const newDirtyStates: Record<string, any> = {};
      campaign.ads.forEach((ad: Ad) => {
        if (prev[ad.id]?.isAdDirty) {
          newDirtyStates[ad.id] = prev[ad.id];
        } else {
          newDirtyStates[ad.id] = {
            isKeywordsDirty: false,
            isHeadlinesDirty: false,
            isDescriptionsDirty: false,
            isAlternativeKeywordsDirty: false,
            isNegativeKeywordsDirty: false,
            isLabelsDirty: false,
            isNameDirty: false,
            isUrlDirty: false,
            isPausedDirty: false,
            isAdDirty: false,
            requiresManualSave: false,
          };
        }
      });
      return newDirtyStates;
    });
  }, [campaign.ads]);

  // Calculate dirty states when adStates change
  useEffect(() => {
    const newDirtyStates: Record<string, any> = {};
    campaign.ads.forEach((ad: Ad) => {
      const adState = adStates[ad.id];
      if (adState) {
        // #4773: adState.keywords is built as active + paused (see the rehydrate effect above), so
        // compare against the same merged set. Comparing against ad.keywords (active only) reported
        // a false-positive dirty whenever the ad had any paused keyword, making Save Changes stick.
        const serverKeywords = [...(ad.keywords || []), ...(ad.paused_keywords || [])];
        // #5623: compare only the persisted/round-trip fields, order-independent. The previous
        // full-object JSON diff stuck the page permanently dirty after an add — a locally-added
        // keyword carries only {text, match_type, id}, while the server returns the full shape
        // (is_paused, is_locked, metrics, keyword_intents...), so the two could never byte-match
        // and Save Changes kept reappearing.
        const normalizeKeywords = (kws: Keyword[]) =>
          kws
            // #5621: drop soft-deleted rows before diffing. A staged delete still registers as
            // dirty (the server list keeps the row until the save lands), while a SAVED delete
            // compares clean — the backend excludes is_deleted rows from reads, so keeping the
            // flagged row on the local side left the diff permanently truthy and Save Changes
            // stuck after saving a delete.
            .filter((k) => !k.is_deleted)
            .map((k) => ({
              id: k.id ?? null,
              text: k.text,
              match_type: (k.match_type ?? '').toUpperCase(),
              is_paused: !!k.is_paused,
              is_locked: !!k.is_locked,
            }))
            .sort(
              (a, b) =>
                (a.id ?? 0) - (b.id ?? 0) ||
                a.text.localeCompare(b.text) ||
                a.match_type.localeCompare(b.match_type),
            );
        const isKeywordsDirty =
          JSON.stringify(normalizeKeywords(adState.keywords)) !== JSON.stringify(normalizeKeywords(serverKeywords));
        // Track headline / description dirtiness via the SAME #5623 convention as
        // keywords: compare only the fields Save Changes actually round-trips
        // ({id, text, funnel membership}), normalized and order-independent. The
        // previous full-object JSON diff wedged Save Changes permanently: it also
        // compared fields Save does not own — type/pin_position persist through
        // their own endpoints the moment they are set (the backend ignores them on
        // update), and the nested ad_funnel_stage carries is_paused, which the
        // stage-pause endpoint owns — so any of them disagreeing with the campaign
        // atom (stale until a background refetch) kept the diff truthy after a
        // clean save (2026-09-07 incident).
        // Staged deletes (is_deleted) are dropped from the local side of the diff — the
        // server list still holds the row until the save lands, so a staged delete reads
        // dirty; once saved, the server excludes the paused row and the diff reads clean.
        // Same shape as the keyword is_deleted handling above.
        const normalizeCopyRows = (rows: Array<{ id?: number; text: string; is_deleted?: boolean; ad_funnel_stage?: { funnel?: string } | null }>) =>
          rows
            .filter((r) => !r.is_deleted)
            .map((r) => ({
              id: r.id != null && r.id > 0 ? r.id : null,
              text: r.text.trim(),
              funnel: r.ad_funnel_stage?.funnel ?? null,
            }))
            .sort((a, b) => (a.id ?? 0) - (b.id ?? 0) || a.text.localeCompare(b.text));
        const isHeadlinesDirty =
          JSON.stringify(normalizeCopyRows(adState.headlines)) !== JSON.stringify(normalizeCopyRows(ad.headlines || []));
        const isDescriptionsDirty =
          JSON.stringify(normalizeCopyRows(adState.descriptions)) !== JSON.stringify(normalizeCopyRows(ad.descriptions || []));
        // Alternatives / negatives / labels join the same #5623 convention: a
        // field-subset, order-independent compare. These lists have NO stable
        // server-side sort (headlines are sorted by id in every response;
        // alternatives are not), so the raw byte-diff wedged Save Changes
        // permanently after a save: the draft held the PUT response's order
        // while the atom held the background refetch's order (2026-09-07,
        // dbg:AlternativeKeywords incident).
        const sortedPick = <T,>(rows: T[] | undefined, pick: (r: T) => unknown) =>
          JSON.stringify((rows || []).map((r) => JSON.stringify(pick(r))).sort());
        const isAlternativeKeywordsDirty =
          sortedPick(adState.alternativeKeywords, (k: Keyword) => ({
            id: k.id ?? null,
            text: (k.text ?? '').trim().toLowerCase(),
            match_type: (k.match_type ?? '').toUpperCase(),
          })) !==
          sortedPick(ad.alternative_keywords, (k: Keyword) => ({
            id: k.id ?? null,
            text: (k.text ?? '').trim().toLowerCase(),
            match_type: (k.match_type ?? '').toUpperCase(),
          }));
        type NegativeRow = { id?: number | null; keyword?: string | null; text?: string | null; match_type?: string | null };
        const pickNegative = (nk: NegativeRow) => ({
          id: nk.id ?? null,
          keyword: ((nk.keyword || nk.text) ?? '').trim().toLowerCase(),
          match_type: (nk.match_type ?? 'EXACT').toUpperCase(),
        });
        const isNegativeKeywordsDirty =
          sortedPick(adState.negativeKeywords, pickNegative) !== sortedPick(ad.negative_keywords, pickNegative);
        type LabelRow = { id?: number | string | null; name?: string | null };
        const pickLabel = (l: LabelRow) => ({ id: l.id ?? null, name: l.name ?? '' });
        const isLabelsDirty = sortedPick(adState.labels, pickLabel) !== sortedPick(ad.labels, pickLabel);
        const adDefaultName = adDisplayName(ad, (campaign.ads?.findIndex((a: Ad) => a.id === ad.id) ?? 0) + 1);
        const isNameDirty = (adState.name || '') !== adDefaultName;
        const isUrlDirty = (adState.url || '') !== (ad.url || '');
        const isPausedDirty = adState.isPaused !== ad.is_paused;
        const isAdDirty = isKeywordsDirty || isHeadlinesDirty || isDescriptionsDirty || isAlternativeKeywordsDirty || isNegativeKeywordsDirty || isLabelsDirty || isNameDirty || isUrlDirty || isPausedDirty;
        // Save Changes button renders whenever the ad is dirty in any persisted field.
        // Nothing auto-saves — headlines and descriptions persist via Save Changes too.
        const requiresManualSave =
          isKeywordsDirty || isAlternativeKeywordsDirty || isNegativeKeywordsDirty || isLabelsDirty || isNameDirty || isHeadlinesDirty || isDescriptionsDirty || isUrlDirty || isPausedDirty;

        newDirtyStates[ad.id] = {
          isKeywordsDirty,
          isHeadlinesDirty,
          isDescriptionsDirty,
          isAlternativeKeywordsDirty,
          isNegativeKeywordsDirty,
          isLabelsDirty,
          isNameDirty,
          isUrlDirty,
          isPausedDirty,
          isAdDirty,
          requiresManualSave,
        };
      }
    });
    setAdDirtyStates(newDirtyStates);
  }, [adStates, campaign.ads]);

  // Polling for ads without headlines lives in campaignQueryOptions'
  // refetchInterval (see query-options.ts, #5066) — no bespoke interval here.

  // Refetch when tasks settle while the ad is open (#6826).
  useRefetchOnTasksSettled(campaignTaskTracking ? { sumTotal, taskProcessing } : null, () => {
    void (async () => {
      await refetchCampaign();
      invalidateQuery(['campaigns']);
    })();
  });
  // On open, refresh a copy older than the freshness window once tasks are settled. Declared after the settle
  // refetch, so that refetch is already in flight when tasks settle (#6826).
  useRefreshCampaignOnView(campaign.slug, sumTotal > 0 && taskProcessing === 0);

  // Add Ad — mirrors the campaign detail page's "Add Ad" flow (#5529 follow-up: surface it here too)
  const handleCreateAd = async () => {
    try {
      const newAd = await createAd(campaign.id, prefixedUrl, selectedCompetitorId ?? undefined);
      setNewlyCreatedAdId(newAd.id);
      setSelectedAdId(newAd.id);
      // Pull the new ad into campaign.ads before navigating, so it appears in the left
      // list and the selection highlights it (otherwise selectedAdId points at an ad not
      // yet in the list → nothing selected).
      await refetchCampaign();
      navigate({ to: `/campaigns/google/${campaign.slug}/ad/${newAd.id}` });
    } catch (err: any) {
      toast({
        description: err?.message || 'Failed to create Ad',
        variant: 'destructive',
      });
      throw err;
    }
  };

  // Handlers
  const handleToggleAdPause = () => {
    if (!selectedAdId) return;
    setAdStates((prev) => ({
      ...prev,
      [selectedAdId]: {
        ...prev[selectedAdId],
        isPaused: !prev[selectedAdId].isPaused,
      },
    }));
  };

  const handleSaveAd = async (adId: number) => {
    const adState = adStates[adId];
    const ad = campaign.ads.find((a: Ad) => a.id === adId);
    if (!ad || !adState) throw new Error('Ad not found');

    const dirtyState = adDirtyStates[adId];

    // Prevent saving with empty ad name
    if (dirtyState?.isNameDirty && !adState.name?.trim()) {
      toast({ description: 'Ad name cannot be empty', variant: 'destructive' });
      throw new Error('Ad name cannot be empty');
    }

    // #5548: a changed landing page URL must be valid and on the same domain (backstop for the
    // inline validation; the backend enforces the domain match too).
    if (dirtyState?.isUrlDirty) {
      const newUrl = (adState.url || '').trim();
      if (!newUrl || !isValidUrl(newUrl) || !isSameHost(newUrl, ad.url)) {
        toast({ description: 'Enter a valid landing page URL on the same domain as the current one.', variant: 'destructive' });
        throw new Error('Invalid landing page URL');
      }
    }

    // Prevent saving case-insensitive duplicate headlines within the same funnel stage.
    // Rows staged for deletion are skipped — they are about to be paused, not kept.
    const activeStateHeadlines = activeRows(adState.headlines);
    const activeStateDescriptions = activeRows(adState.descriptions);
    const normalizeText = dedupeKey;
    const hasFunnelStages = activeStateHeadlines.some((h: Headline) => h.ad_funnel_stage);
    const headlinesByStage = new Map<string | null, Array<{ text: string; normalized: string }>>();
    for (const h of activeStateHeadlines) {
      const key = h.ad_funnel_stage?.funnel ?? null;
      const bucket = headlinesByStage.get(key) ?? [];
      bucket.push({ text: h.text, normalized: normalizeText(h.text) });
      headlinesByStage.set(key, bucket);
    }
    const duplicateNormalized = new Set<string>();
    const firstDupePerKey: Array<{ text: string; funnel: string | null }> = [];
    for (const [funnel, items] of headlinesByStage) {
      const seen = new Map<string, string>();
      for (const { text, normalized } of items) {
        if (seen.has(normalized)) {
          if (!duplicateNormalized.has(normalized)) {
            firstDupePerKey.push({ text: seen.get(normalized)!, funnel });
            duplicateNormalized.add(normalized);
          }
        } else {
          seen.set(normalized, text);
        }
      }
    }
    if (duplicateNormalized.size > 0) {
      const parts = firstDupePerKey.map(({ text, funnel }) => {
        const stage = funnel && hasFunnelStages ? ` (${getFunnelStageLabel(funnel as FunnelStage)})` : '';
        return `"${text}"${stage}`;
      });
      const message = parts.length === 1
        ? `${parts[0]} is duplicated${firstDupePerKey[0].funnel && hasFunnelStages ? ` in ${getFunnelStageLabel(firstDupePerKey[0].funnel as FunnelStage)}` : ''}. Remove one before saving.`
        : `Duplicate headlines: ${parts.join(', ')}. Remove duplicates before saving.`;
      toast({ description: message, variant: 'destructive' });
      setDuplicateHeadlineTexts(duplicateNormalized);
      throw new Error(message);
    }

    // Google Ads Responsive Search Ad minimums (MIN_HEADLINES=3, MIN_DESCRIPTIONS=2). Block the
    // save — and the doomed background Google Ads sync — before it leaves the app, mirroring the
    // empty-name / duplicate guards above. Without this, Google Ads silently rejects the push and
    // the user only finds out via the delayed sync-failure toast (#5598).
    const MIN_HEADLINES = 3;
    const MIN_DESCRIPTIONS = 2;
    const countableHeadlines = activeStateHeadlines.filter((h) => !hasUnfallbackedTag(h.text));
    if (countableHeadlines.length < MIN_HEADLINES) {
      const msg = `Ads need at least ${MIN_HEADLINES} headlines to publish to Google Ads (this ad has ${countableHeadlines.length}).`;
      toast({ description: msg, variant: 'destructive' });
      throw new Error(msg);
    }
    const countableDescriptions = activeStateDescriptions.filter((d) => !hasUnfallbackedTag(d.text));
    if (countableDescriptions.length < MIN_DESCRIPTIONS) {
      const msg = `Ads need at least ${MIN_DESCRIPTIONS} descriptions to publish to Google Ads (this ad has ${countableDescriptions.length}).`;
      toast({ description: msg, variant: 'destructive' });
      throw new Error(msg);
    }

    setIsAdSavingLocal(true);
    try {
      // Concurrent edit check for name changes
      if (dirtyState?.isNameDirty) {
        const freshCampaign = await queryClient.fetchQuery({ ...campaignQueryOptions(campaign.slug), staleTime: 0 });
        const freshAd = freshCampaign?.ads?.find((a: Ad) => a.id === adId);
        if (freshAd && (freshAd.name || null) !== (ad.name || null)) {
          const msg = `This ad was renamed to "${freshAd.name || 'unnamed'}" by someone else. Please review and try again.`;
          toast({ description: msg });
          await refetchCampaign();
          throw new Error(msg);
        }
      }

      // #4625-followup: unsaved headlines/descriptions get a temp negative
      // id stamped client-side so delete/propagate can target them by id.
      // Strip those ids before sending — the backend treats missing id as
      // "create new" and would otherwise try to update a non-existent row.
      const stripTempId = <T extends { id?: number }>(item: T): T =>
        item.id !== undefined && item.id < 0 ? { ...item, id: undefined } : item;

      const updatedAd: Ad = {
        ...ad,
        name: dirtyState?.isNameDirty ? (adState.name?.trim() || null) : ad.name,
        url: dirtyState?.isUrlDirty ? (adState.url?.trim() || ad.url) : ad.url,
        keywords: adState.keywords,
        headlines: adState.headlines.map(stripTempId),
        descriptions: adState.descriptions.map(stripTempId),
        alternative_keywords: adState.alternativeKeywords,
        negative_keywords: adState.negativeKeywords,
        labels: adState.labels,
        state: AD_STATE.DRAFT,
      };

      const savedAd = await updateAdMutation.mutateAsync({
        campaignId: campaign.id,
        ad: updatedAd,
      });

      // Write the server's saved ad straight into the campaign cache so the in-memory view is
      // fresh immediately — before any navigate-away (the unsaved-changes prompt's proceed()
      // remounts the ad route). Synchronous, no refetch, no race. The background refresh below
      // still runs for broader state (task tracking, live-sync side effects). #5598.
      queryClient.setQueryData(['campaign', campaign.slug], (old: CampaignData | undefined) =>
        old ? { ...old, ads: old.ads.map((a: Ad) => (a.id === savedAd.id ? savedAd : a)) } : old,
      );

      // Re-baseline the local draft from the saved ad. Locally-added headlines/descriptions
      // carry temp negative ids and a partial shape; the server row comes back with a real id
      // and the full shape (rating, funnel stage...). Without this re-snapshot the dirty
      // recompute's JSON diff stays truthy after the save, flips the flags back on, and the
      // rehydrate effect then preserves the stale draft as an "in-progress edit" — Save
      // Changes reappears forever after adding a row (same loop #5623 hit for keywords).
      setAdStates((prev) => ({
        ...prev,
        [adId]: {
          ...prev[adId],
          keywords: [...(savedAd.keywords || []), ...(savedAd.paused_keywords || [])],
          headlines: savedAd.headlines || [],
          descriptions: savedAd.descriptions || [],
          alternativeKeywords: savedAd.alternative_keywords || [],
          negativeKeywords: savedAd.negative_keywords || [],
          labels: savedAd.labels || [],
          name: savedAd.name || prev[adId]?.name,
        },
      }));

      if (dirtyState?.isPausedDirty) {
        if (adState.isPaused) {
          await pauseAd(campaign.id, adId);
        } else {
          await resumeAd(campaign.id, adId);
        }
      }

      // Clear dirty state and show the "Saved ✓" tick immediately so the
      // spinner -> tick handover commits in one frame (#4388). Previously the
      // tick only appeared after `await refetchCampaign()`, so on real network
      // latency the spinner stopped and the tick lagged behind by the whole
      // post-save chain. The refetch/invalidate below are not user-blocking, so
      // they now run in the background after the tick has shown.
      setAdDirtyStates((prev) => ({
        ...prev,
        [adId]: {
          isKeywordsDirty: false,
          isHeadlinesDirty: false,
          isDescriptionsDirty: false,
          isAlternativeKeywordsDirty: false,
          isNegativeKeywordsDirty: false,
          isLabelsDirty: false,
          isNameDirty: false,
          isUrlDirty: false,
          isPausedDirty: false,
          isAdDirty: false,
          requiresManualSave: false,
        },
      }));
      setDuplicateHeadlineTexts(new Set());
      setAdSaveSuccess(true);
      window.setTimeout(() => setAdSaveSuccess(false), 2000);
      posthog?.capture('ad_updated', { adId });

      // Non-blocking: keep the cache fresh after the tick has already rendered.
      void (async () => {
        try {
          await refetchCampaign();
          queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
        } catch {
          // The save already succeeded; a stale-cache refresh failure is non-fatal.
        }
      })();
    } catch (error) {
      // The server's `detail` explains what to do about it ("no scraped data for URL…"); the
      // generic fallback hid that and made a specific 400 look like an outage (#6253). Prefer the
      // embedded JSON detail, then the raw message, and only then the generic string.
      let errorMessage = 'Failed to update ad';
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
        try {
          const jsonMatch = error.message.match(/\{.*\}/);
          if (jsonMatch) {
            const errorData = JSON.parse(jsonMatch[0]);
            // Read through the one FastAPI-detail reader: a list or object here was toasted raw (#6809).
            errorMessage = extractApiErrorDetail(errorData.detail) ?? errorMessage;
          }
        } catch {
          errorMessage = error.message;
        }
      }
      toast({ description: errorMessage, variant: 'destructive' });
      posthog?.capture('ad_update_failed', { adId, error });
      throw new Error(errorMessage);
    } finally {
      setIsAdSavingLocal(false);
    }
  };

  // #5548: changing the landing page URL needs explicit confirmation, and the new URL must be valid
  // and on the same domain. Gate both save entry points (the Save Changes button and the
  // unsaved-changes guard) through here; the warning modal's confirm calls handleSaveAd directly.
  const requestAdSave = (adId: number): Promise<void> => {
    const dirtyState = adDirtyStatesRef.current[adId];
    if (dirtyState?.isUrlDirty) {
      const ad = campaign.ads.find((a: Ad) => a.id === adId);
      const newUrl = (adStates[adId]?.url || '').trim();
      if (!newUrl || !isValidUrl(newUrl) || !isSameHost(newUrl, ad?.url || '')) {
        toast({ description: 'Enter a valid landing page URL on the same domain as the current one.', variant: 'destructive' });
        return Promise.reject(new Error('Invalid landing page URL'));
      }
      setUrlWarningAdId(adId);
      // Reject so the unsaved-changes guard waits for the user to confirm rather than navigating away.
      return Promise.reject(new Error('URL change requires confirmation'));
    }
    return handleSaveAd(adId);
  };

  const confirmUrlSave = () => {
    const adId = urlWarningAdId;
    setUrlWarningAdId(null);
    // handleSaveAd surfaces its own failures via toast before re-throwing, so the rejection is swallowed
    // here only to avoid an unhandled promise rejection — the user has already been notified.
    if (adId != null) void handleSaveAd(adId).catch(() => {});
  };

  const updateAdState = (adId: number, field: string, value: any) => {
    setAdStates((prev) => ({
      ...prev,
      [adId]: {
        ...prev[adId],
        [field]: typeof value === 'function' ? value((prev[adId] as any)[field]) : value,
      },
    }));
  };

  const handleGenerateHeadlines = async (funnelStage: FunnelStage) => {
    if (!selectedAd || !selectedAdId) return;
    const dirtyState = adDirtyStates[selectedAdId];

    if (dirtyState?.isHeadlinesDirty) {
      toast({
        description: 'Please save your headline changes before generating new ones',
        variant: 'destructive'
      });
      return;
    }

    const hasKeywords = selectedAdState?.keywords.some((k: Keyword) => !k.is_paused);
    if (!hasKeywords) {
      toast({
        description: 'Add a keyword first to generate headlines',
        variant: 'destructive'
      });
      return;
    }

    const currentHeadlines = selectedAd.headlines.filter(
      (h: Headline) => h.ad_funnel_stage?.funnel === funnelStage
    );
    const missingCount = 15 - currentHeadlines.length;

    if (missingCount <= 0) {
      toast({
        description: 'This funnel stage already has the maximum number of headlines (15)',
        variant: 'default'
      });
      return;
    }

    setIsGeneratingHeadlines(true);
    try {
      const newHeadlines = await generateAllHeadlines(campaign.id, selectedAdId, funnelStage, missingCount);

      if (!Array.isArray(newHeadlines)) {
        throw new Error('Invalid response from server');
      }

      if (newHeadlines.length === 0) {
        toast({
          description: 'No headlines were generated. Please try again. If the problem persists, check that your business description and keywords are set up correctly.',
          variant: 'destructive',
        });
        return;
      }

      const newIds = new Set(
        newHeadlines
          .map((h: Headline) => h.id)
          .filter((id): id is number => id !== undefined)
      );

      await refetchCampaign();

      setNewlyGeneratedHeadlineIds(newIds);

      setTimeout(() => {
        setNewlyGeneratedHeadlineIds(new Set());
      }, 5000);

    } catch (error) {
      console.error('Error generating headlines:', error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to generate headlines',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingHeadlines(false);
    }
  };

  const handleGenerateDescriptions = async (funnelStage: FunnelStage) => {
    if (!selectedAd || !selectedAdId) return;
    const dirtyState = adDirtyStates[selectedAdId];

    if (dirtyState?.isDescriptionsDirty) {
      toast({
        description: 'Please save your description changes before generating new ones',
        variant: 'destructive'
      });
      return;
    }

    const currentDescriptions = selectedAd.descriptions?.filter(
      (d: Description) => d.ad_funnel_stage?.funnel === funnelStage
    ) ?? [];
    const missingCount = 4 - currentDescriptions.length;

    if (missingCount <= 0) {
      toast({
        description: 'This funnel stage already has the maximum number of descriptions (4)',
        variant: 'default'
      });
      return;
    }

    setIsGeneratingDescriptions(true);
    try {
      await generateAllDescriptions(campaign.id, selectedAdId, funnelStage, missingCount);

      await refetchCampaign();

      toast({ description: 'Descriptions generated successfully!' });
    } catch (error) {
      console.error('Error generating descriptions:', error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to generate descriptions',
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingDescriptions(false);
    }
  };

  // Ad name focus state (for showing helper text)
  const [isAdNameFocused, setIsAdNameFocused] = useState(false);

  const [rebuildingAdUrl, setRebuildingAdUrl] = useState<string | null>(null);
  const rebuildingOldAdIdRef = useRef<number | null>(null);

  const handleRestartAd = () => {
    // Capture the URL and ID before the ad gets deleted so we can find the new ad later
    const adUrl = selectedAd?.url;
    restartAd(undefined, {
      onSuccess: () => {
        if (adUrl) {
          setRebuildingAdUrl(adUrl);
          rebuildingOldAdIdRef.current = selectedAdId;
          setRebuildingAdId(selectedAdId);
        }
        queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
        refetchCampaign();
      },
      onError: (error) => {
        toast({
          description: `Failed to rebuild ad: ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: 'destructive',
        });
      },
    });
  };

  // After rebuild, the old ad is deleted and a new one is created with a new ID.
  // Highlight the new ad in the sidebar immediately, then navigate once it has content.
  useEffect(() => {
    if (!rebuildingAdUrl) return;

    const oldAdId = rebuildingOldAdIdRef.current;
    const newAd = campaign.ads?.find((ad: Ad) => ad.url === rebuildingAdUrl && ad.id !== oldAdId);
    if (newAd) {
      // Highlight in sidebar immediately and show spinner on the new ad
      if (selectedAdId !== newAd.id) {
        setSelectedAdId(newAd.id);
        setRebuildingAdId(newAd.id);
      }

      // Navigate once the new ad has content loaded
      const hasContent = (newAd.keywords?.length ?? 0) > 0 && (newAd.headlines?.length ?? 0) > 0;
      if (hasContent || areAllTasksCompleted) {
        setRebuildingAdUrl(null);
        rebuildingOldAdIdRef.current = null;
        setRebuildingAdId(null);
        navigate({ to: `/campaigns/google/${campaign.slug}/ad/${newAd.id}` });
      }
    }
  }, [rebuildingAdUrl, campaign.ads, selectedAdId, campaign.slug, navigate, areAllTasksCompleted, setSelectedAdId]);

  const handleTabChange = (newSection: string) => {
    setPersistedAdTab(newSection);
    navigate({
      to: `/campaigns/google/${campaign.slug}/ad/${selectedAdId}`,
      hash: newSection === 'overview' ? '' : newSection,
    });
  };

  // Get current ad state
  const selectedAdState = selectedAdId ? adStates[selectedAdId] : null;
  const selectedDirtyState = selectedAdId ? adDirtyStates[selectedAdId] : null;

  // Ad completeness check
  const getAdCompleteness = (ad: Ad) => {
    const keywordCount = ad.keywords?.length ?? 0;
    const funnelStageIds = new Set<number>();
    const adFunnelStages = ad.ad_funnel_stage;

    if (adFunnelStages?.length) {
      adFunnelStages.forEach(fs => fs?.id && funnelStageIds.add(fs.id));
    } else {
      ad.headlines?.forEach(h => h.ad_funnel_stage?.id && funnelStageIds.add(h.ad_funnel_stage.id));
      ad.descriptions?.forEach(d => d.ad_funnel_stage?.id && funnelStageIds.add(d.ad_funnel_stage.id));
    }

    let headlineCount = 0;
    let descriptionCount = 0;

    if (funnelStageIds.size === 0) {
      headlineCount = ad.headlines?.length ?? 0;
      descriptionCount = ad.descriptions?.length ?? 0;
    } else {
      let minHeadline = Infinity;
      let minDescription = Infinity;

      for (const stageId of funnelStageIds) {
        const stageHeadlines = ad.headlines?.filter(h => h.ad_funnel_stage?.id === stageId) ?? [];
        const stageDescriptions = ad.descriptions?.filter(d => d.ad_funnel_stage?.id === stageId) ?? [];

        minHeadline = Math.min(minHeadline, stageHeadlines.length);
        minDescription = Math.min(minDescription, stageDescriptions.length);
      }

      headlineCount = minHeadline === Infinity ? 0 : minHeadline;
      descriptionCount = minDescription === Infinity ? 0 : minDescription;
    }

    return { keywordCount, headlineCount, descriptionCount };
  };

  // Check if ad has missing data
  const adCompleteness = selectedAd ? getAdCompleteness(selectedAd) : null;
  const hasMissingData = adCompleteness && (
    adCompleteness.keywordCount === 0 ||
    adCompleteness.headlineCount === 0 ||
    adCompleteness.descriptionCount === 0
  );

  // Use the layout's polling atom rather than the local useQuery to avoid the race condition
  // where task tracking hasn't caught up yet immediately after Add Ad navigation.
  const hasActiveAdGenerationTasks = (taskTrackingFromAtom?.ad_generation?.in_progress ?? 0) > 0;
  // The ad's own state is the authoritative signal now that a crashed generation marks it ERROR
  // (#6253). Task tracking stays as the fallback: ads stranded before that fix are still DRAFT.
  const isAdInErrorState = selectedAd?.state === AD_STATE.ERROR;
  const hasAdGenerationFailed =
    (isAdInErrorState || (taskTrackingFromAtom?.ad_generation?.failed ?? 0) > 0) && !hasActiveAdGenerationTasks;
  const isRebuilding = isRestartingAd || !!rebuildingAdUrl;
  const isThisAdNewlyCreated = newlyCreatedAdId === selectedAdId;
  // Show generating state when: ad has missing data AND (tracking not loaded yet, OR tasks are
  // running, OR no ad_generation tasks have been registered yet — i.e. task just queued,
  // OR this ad was just created and tasks haven't registered yet).
  // Never show generating when generation has already failed.
  const isAdGenerating = !hasAdGenerationFailed && !!hasMissingData && (
    !taskTrackingFromAtom ||
    hasActiveAdGenerationTasks ||
    (taskTrackingFromAtom.ad_generation?.total ?? 0) === 0 ||
    isThisAdNewlyCreated
  );

  // Clear the newly created flag once tasks become active OR if generation failed before
  // in_progress was ever observed (fast failure — tracking may skip the in_progress=1 poll).
  useEffect(() => {
    if (isThisAdNewlyCreated && (hasActiveAdGenerationTasks || hasAdGenerationFailed)) {
      setNewlyCreatedAdId(null);
    }
  }, [isThisAdNewlyCreated, hasActiveAdGenerationTasks, hasAdGenerationFailed, setNewlyCreatedAdId]);

  // Toast once when ad generation transitions to failed.
  const prevAdGenFailedRef = useRef(false);
  useEffect(() => {
    if (hasAdGenerationFailed && !prevAdGenFailedRef.current) {
      toast({
        description: 'Ad generation failed. Please try again.',
        variant: 'destructive',
      });
    }
    prevAdGenFailedRef.current = hasAdGenerationFailed;
  }, [hasAdGenerationFailed, toast]);
  const shouldShowFailedBanner =
    (isAdInErrorState || (areAllTasksCompleted && !!hasMissingData)) && !isRebuilding && !isAdGenerating;
  const shouldShowLoadingSkeleton = isRebuilding;

  return (
    <div className='flex flex-col h-full'>
      {UnsavedChangesModal}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <GoLivePaymentGate />
        <div className="grid grid-cols-12 gap-1">
          {/* Left: Campaigns List */}
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'Campaigns', isActive: true },
                { id: 'new', label: '+ Add', onClick: () => navigate({ to: '/new/campaign' }) },
              ]}
              actions={
                <CampaignSidebarToolbar
                  filterTypes={filterTypes}
                  filterStatuses={filterStatuses}
                  filterPlatforms={filterPlatforms}
                  showPlatforms
                  onFiltersChange={updateCampaignFilters}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar filterTypes={filterTypes} filterStatuses={filterStatuses} />
            </TabCardPanel>
          </div>

          {/* Right: Ad Content */}
          <div className="col-span-9">

            <div className="flex items-end justify-between">
              <div className="flex items-end gap-1">
                {(['overview', 'headlines', 'descriptions', 'keywords', 'negatives', 'labels'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`${tabButtonClass(section === tab)} cursor-pointer`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pb-1">
                {adSaveSuccess && (
                  <span className="h-9 pr-2 text-sm text-[#047857] flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Saved
                  </span>
                )}
                <CampaignStatusMessages
                  firstPublishedAt={campaign.first_published_at}
                  state={campaign.state}
                  isLaunching={isGoingLive}
                  transition={campaignStates.toggleTransition}
                  onBusyChange={setStatusBusy}
                />

                {/* Save Changes button - appears when ad has unsaved changes
                    in a non-auto-saving field. Headlines/descriptions auto-save
                    on blur so they are excluded here (see requiresManualSave). */}
                {!isReadOnly && selectedDirtyState?.requiresManualSave && (
                  <SaveChangesButton
                    onClick={() => { void requestAdSave(selectedAdId!).catch(() => {}); }}
                    saving={isAdSavingLocal || updateAdMutation.isPending}
                    className='h-9 shadow-lg animate-bounce-in-right'
                  />
                )}
                {/* `|| isGoingLive` keeps the button mounted as "Going Live..." for the whole
                    launch: the state service commits LIVE + first_published_at at task start,
                    which would otherwise unmount the button mid-launch (#5663). */}
                {!isReadOnly && !hasGenerationFailed && !selectedDirtyState?.isAdDirty &&
                  ((!isStillGenerating && isAwaitingFirstPublish(campaign) &&
                    (campaign.state === CAMPAIGN_STATE.DRAFT || campaign.state === CAMPAIGN_STATE.PAUSED)) ||
                    isGoingLive) && (
                  <Button
                    onClick={goLive}
                    disabled={isGoingLive}
                    title={blockingReason ?? undefined}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    {isGoingLive ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Going Live...
                      </>
                    ) : (
                      <>
                        Go Live
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                )}
                {/* Deploy changes: a campaign with pending changes not yet pushed to Google Ads
                    (e.g. an ad just added here). Same trigger/handler as the campaign page.
                    Stays visible while generating (#5637) — clicking surfaces a "still
                    generating" toast from useDeployChanges instead of hiding the button. */}
                {/* !isForeignTransitionPending: no Deploy beside a launch in flight (#5825). */}
                {!isReadOnly && !hasGenerationFailed && !selectedDirtyState?.isAdDirty && ((hasPendingChanges && !isForeignTransitionPending) || isDeploying) && (
                  <Button
                    onClick={handleDeployChanges}
                    disabled={deployDisabled}
                    title={overBudgetTitle}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    {isDeploying ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Deploying...
                      </>
                    ) : (
                      <>
                        Deploy
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                )}
                {/* Sequential with the status slot (#5669): hidden while the toggle
                    transition spinner or a confirmation is showing, so exactly one
                    element occupies this spot at a time. */}
                {!isReadOnly && !hasGenerationFailed && !isStillGenerating && !selectedDirtyState?.isAdDirty &&
                  canResumeInGlitch(campaign) && !campaignStates.isTogglePending && !statusBusy && (
                  <Button
                    onClick={campaignActions.handleTogglePause}
                    disabled={campaignStates.isLoading}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    Resume
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {/* Ad Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' className='h-9 gap-2'>
                      Ad
                      <ChevronDown className='h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end' className='min-w-[160px] shadow-lg rounded-lg border border-slate-100'>
                    {/* Add Ad — same flow as the campaign-level dropdown */}
                    <DropdownMenuItem
                      onClick={() => setIsCreateAdModalOpen(true)}
                      disabled={isReadOnly || isStillGenerating}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${isReadOnly || isStillGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Plus className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                      Add Ad
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowAdPreviewModal(true)}
                      disabled={!selectedAdId}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${!selectedAdId ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Eye className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                      Preview Ad
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleToggleAdPause}
                      disabled={isReadOnly || !selectedAdId}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${isReadOnly || !selectedAdId ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {(selectedAdState?.isPaused ?? selectedAd?.is_paused) ? (
                        <Play className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                      ) : (
                        <Pause className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                      )}
                      {(selectedAdState?.isPaused ?? selectedAd?.is_paused) ? 'Resume Ad' : 'Pause Ad'}
                    </DropdownMenuItem>
                    {!isReadOnly && !isBrand && getActiveFunnelStage() && (section === 'headlines' || section === 'descriptions') && (
                      <DropdownMenuItem
                        onClick={() => {
                          const fs = getActiveFunnelStage();
                          if (fs?.id) handleToggleFunnelStagePause(fs.id, !!fs.is_paused);
                        }}
                        disabled={pauseFunnelStageMutation.isPending || resumeFunnelStageMutation.isPending || selectedAd?.is_paused}
                        className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${selectedAd?.is_paused ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {getActiveFunnelStage()?.is_paused ? (
                          <Play className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                        ) : (
                          <PauseCircle className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                        )}
                        {getActiveFunnelStage()?.is_paused ? 'Resume Funnel Stage' : 'Pause Funnel Stage'}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => {
                        const adGroupId = selectedAd?.googleads_ad_group?.googleads_ad_group_id;
                        const campaignGoogleId = campaign?.googleads_campaign?.googleads_campaign_id;
                        const customerId = campaign?.googleads_account?.googleads_account_id;
                        if (adGroupId && campaignGoogleId && customerId) {
                          window.open(
                            `https://ads.google.com/aw/adgroups/ads?adGroupId=${adGroupId}&campaignId=${campaignGoogleId}&customerId=${customerId}`,
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }
                      }}
                      disabled={!selectedAd?.googleads_ad_group?.googleads_ad_group_id || !campaign?.googleads_campaign?.googleads_campaign_id}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${!selectedAd?.googleads_ad_group?.googleads_ad_group_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <ExternalLink className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
                      View in Google Ads
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeleteAdConfirm(true)}
                      disabled={isReadOnly || !selectedAdId || deleteAdMutation.isPending}
                      className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 text-red-600 ${isReadOnly || !selectedAdId || deleteAdMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {deleteAdMutation.isPending ? (
                        <Spinner size="sm" className="shrink-0 mr-2" />
                      ) : (
                        <Trash className='h-4 w-4 shrink-0 mr-2' />
                      )}
                      {deleteAdMutation.isPending ? 'Deleting...' : 'Delete Ad'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <Card className="rounded-lg rounded-tl-none border bg-white">
              <CardContent className="p-6">
                {/* #6448: the cross-org/cross-account read-only banner was removed — the
                    backend now 404s wrong-org campaigns. Same-org read-only causes keep a
                    notice here, mirroring the campaign detail page. */}
                {isArchived && (
                  <div className="bg-gray-50 border-l-4 border-l-gray-400 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Archived Campaign</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          This campaign is archived and read-only. Use the campaign menu in <span className="font-medium">Overview</span> to restore it.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* #6573: adoption clears this. origin stays 'external' for good, so gating on
                    isExternal alone left a permanent read-only warning over a page that is fully
                    editable once managed_at is stamped, which is exactly what importing does. */}
                {!isArchived && isExternal && !isManagedExternal && (
                  <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-900">
                        This campaign is managed in Google Ads and is read-only in Glitch.
                      </p>
                    </div>
                  </div>
                )}
                {/* Failed banner — shown on all tabs */}
                {shouldShowFailedBanner && adCompleteness && selectedAd && (
                  <AdFailedBanner
                    errorMessage={`Missing essential data. Keywords: ${adCompleteness.keywordCount}, Headlines: ${adCompleteness.headlineCount}, Descriptions: ${adCompleteness.descriptionCount}`}
                    onRestart={handleRestartAd}
                    isRestarting={isRestartingAd}
                  />
                )}
                {/* Generating Ad banner — shown on all tabs when ad has no content and generation is in flight */}
                {isAdGenerating && (
                  <div className="bg-[#EDE9FE] border-l-4 border-l-[#290BD9] rounded-lg p-6 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-white rounded-lg shadow p-3">
                        <Spinner size="lg" className="text-[#290BD9]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Ad</h3>
                        <p className="text-gray-900 mb-3">
                          Your ad is currently being generated. This can take 1-2 minutes.
                        </p>
                        {campaignTaskTracking && (() => {
                          const tasks = ['keywords_generation', 'headline_generation', 'descriptions_generation'] as const;
                          let sumTotal = 0;
                          let sumCompleted = 0;
                          tasks.forEach(taskKey => {
                            const task = campaignTaskTracking[taskKey];
                            if (task && 'total' in task && 'completed' in task) {
                              sumTotal += task.total;
                              sumCompleted += task.completed;
                            }
                          });
                          return (
                            <div className="mt-4">
                              <div className="w-3/5 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-[#290BD9] h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${sumTotal > 0 ? (sumCompleted / sumTotal) * 100 : 0}%` }}
                                />
                              </div>
                              <p className="text-sm text-gray-700 mt-1">
                                {sumTotal > 0 ? Math.round((sumCompleted / sumTotal) * 100) : 0}% complete
                              </p>
                              <div className="mt-3 space-y-2">
                                {campaignTaskTracking.keywords_generation?.total > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-900">Keywords</span>
                                    <span className="text-gray-900">
                                      {campaignTaskTracking.keywords_generation.completed}/{campaignTaskTracking.keywords_generation.total}
                                      {campaignTaskTracking.keywords_generation.in_progress > 0 && (
                                        <span className="text-[#290BD9] ml-1">(+{campaignTaskTracking.keywords_generation.in_progress} in progress)</span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                {campaignTaskTracking.headline_generation?.total > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-900">Headlines</span>
                                    <span className="text-gray-900">
                                      {campaignTaskTracking.headline_generation.completed}/{campaignTaskTracking.headline_generation.total}
                                      {campaignTaskTracking.headline_generation.in_progress > 0 && (
                                        <span className="text-[#290BD9] ml-1">(+{campaignTaskTracking.headline_generation.in_progress} in progress)</span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                {campaignTaskTracking.descriptions_generation?.total > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-900">Descriptions</span>
                                    <span className="text-gray-900">
                                      {campaignTaskTracking.descriptions_generation.completed}/{campaignTaskTracking.descriptions_generation.total}
                                      {campaignTaskTracking.descriptions_generation.in_progress > 0 && (
                                        <span className="text-[#290BD9] ml-1">(+{campaignTaskTracking.descriptions_generation.in_progress} in progress)</span>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* === OVERVIEW TAB === */}
                {section === 'overview' && selectedAd && (() => {
                  const adIndex = (campaign.ads?.findIndex((a: Ad) => a.id === selectedAd.id) ?? 0) + 1;
                  const adName = adDisplayName(selectedAd, adIndex);
                  // Always render funnel stages in funnel order (Top -> Middle -> Bottom).
                  // Backend response order isn't guaranteed; sort with the shared helper. #4348
                  const funnelStages = visibleFunnelStages(selectedAd.ad_funnel_stage);
                  const adIntent = selectedAd.ad_intents?.[0];
                  return (
                    <div className="space-y-6">
                      {/* Summary metrics for this ad */}
                      {(() => {
                        const stageCount = Math.max(funnelStages.length, 1);
                        const maxHeadlines = 15 * stageCount;
                        const maxDescriptions = 4 * stageCount;
                        const isGenerating = isStillGenerating || hasActiveAdGenerationTasks;
                        const metrics: { label: string; current: number; max: number | null; tab: AdSection | null }[] = [
                          { label: 'Headlines', current: selectedAd.headlines?.length || 0, max: maxHeadlines, tab: 'headlines' },
                          { label: 'Descriptions', current: selectedAd.descriptions?.length || 0, max: maxDescriptions, tab: 'descriptions' },
                          { label: 'Keywords', current: selectedAd.keywords?.length || 0, max: 15, tab: 'keywords' },
                          { label: 'Negatives', current: selectedAd.negative_keywords?.length || 0, max: null, tab: 'negatives' },
                          { label: 'Angles', current: funnelStages.length, max: null, tab: null },
                          { label: 'Labels', current: selectedAdState?.labels?.length ?? 0, max: null, tab: 'labels' },
                        ];
                        // 7 cards laid out as:
                        // - narrow (<1400px): row 1 = 3 cards (col-span-4), row 2 = 4 cards (col-span-3) — fills a 12-col grid evenly
                        // - wide (≥1400px): all 7 on a single row via grid-cols-7 with col-span-1 each
                        // `flex flex-col items-center` forces explicit horizontal centering so wider labels
                        // like DESCRIPTIONS don't appear left-shifted relative to their numbers.
                        const cardBaseClass = 'flex flex-col items-center justify-center rounded-lg border bg-white p-4 text-center transition-colors';
                        const colSpan = (idx: number) => `${idx < 3 ? 'col-span-4' : 'col-span-3'} min-[1400px]:col-span-1`;
                        return (
                          <div className="grid grid-cols-12 min-[1400px]:grid-cols-7 gap-4">
                            {metrics.map((metric, idx) => {
                              const content = (
                                <>
                                  <p className={`text-xs font-medium uppercase tracking-wide transition-colors ${metric.tab ? 'text-gray-500 group-hover/card:text-[#290BD9] group-hover/card:underline' : 'text-gray-500'}`}>{metric.label}</p>
                                  <p className={`text-2xl font-semibold mt-1 transition-colors ${metric.tab ? 'text-gray-900 group-hover/card:text-[#290BD9]' : 'text-gray-900'}`}>
                                    {isGenerating && metric.current === 0 && metric.max !== null ? (
                                      <Spinner size="md" className="text-gray-400 inline-block" />
                                    ) : (
                                      metric.max !== null ? `${metric.current}/${metric.max}` : metric.current
                                    )}
                                  </p>
                                </>
                              );
                              return metric.tab ? (
                                <button
                                  key={metric.label}
                                  onClick={() => handleTabChange(metric.tab!)}
                                  className={`${colSpan(idx)} ${cardBaseClass} cursor-pointer group/card`}
                                >
                                  {content}
                                </button>
                              ) : (
                                <div key={metric.label} className={`${colSpan(idx)} ${cardBaseClass}`}>
                                  {content}
                                </div>
                              );
                            })}
                            <div className={`${colSpan(6)} ${cardBaseClass}`}>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                              <p className="mt-2">
                                {isGenerating ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EDE9FE] text-[#290BD9]">Generating</span>
                                ) : selectedAd.is_paused ? (
                                  // #4717: the ad itself is paused (independent of campaign state).
                                  (<TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 cursor-default">Paused</span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-[220px] text-xs">This ad is paused and will not serve.</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>)
                                ) : campaign.state === CAMPAIGN_STATE.LIVE ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                                ) : campaign.state === CAMPAIGN_STATE.PAUSED ? (
                                  // #4717: the ad is not individually paused, but its campaign is — pausing a
                                  // campaign does not pause its ads (they stay enabled but do not serve), so
                                  // distinguish this campaign-driven suspension from an ad-level pause and from
                                  // a genuine draft rather than mislabeling a live ad as "Draft".
                                  (<TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 cursor-default">
                                          Paused<span className="font-normal text-amber-700/70">· Campaign</span>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-[220px] text-xs">This ad will not serve while its campaign is paused.</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>)
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Draft</span>
                                )}
                              </p>
                            </div>
                          </div>
                        )
                      })()}
                      {/* Ad info layout:
                          - 2-col grid: metadata fields on the left, Funnel Stages cards on the right
                          - Labels: full width below the grid
                          - Intent: full width below labels (rendered as a sibling outside this block) */}
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                          {/* Left column — metadata */}
                          <div className="space-y-6">
                            <div>
                              <h3 className="text-slate-800 font-semibold mb-2">Glitch Ad Name</h3>
                              <Input
                                value={selectedAdState?.name ?? adName}
                                onChange={(e) => {
                                  if (e.target.value.length <= 100 && selectedAdId) {
                                    updateAdState(selectedAdId, 'name', e.target.value);
                                  }
                                }}
                                onFocus={() => setIsAdNameFocused(true)}
                                onBlur={() => setIsAdNameFocused(false)}
                                maxLength={100}
                                placeholder="Enter ad name"
                                className="!ring-inset text-gray-900 disabled:opacity-100 disabled:text-gray-900"
                                style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                disabled={isReadOnly}
                                readOnly={isReadOnly}
                              />
                              {isAdNameFocused && (
                                <div className="flex items-center justify-between mt-1 px-1">
                                  <span className="text-xs text-gray-400">This ad name only applies in Glitch</span>
                                  <span className={`text-xs ${(selectedAdState?.name?.length || 0) >= 90 ? 'text-amber-500' : 'text-gray-400'}`}>
                                    {selectedAdState?.name?.length || 0}/100
                                  </span>
                                </div>
                              )}
                            </div>

                            {(() => {
                              const currentGroupName =
                                selectedAd.googleads_ad_group?.name
                                ?? selectedAd.googleads_ad_group_name
                                ?? '';
                              const hasEdit = selectedAdId != null && selectedAdId in adGroupNameEdits;
                              const editedValue = hasEdit ? adGroupNameEdits[selectedAdId!] : currentGroupName;
                              const isDirty = editedValue !== currentGroupName;
                              // Error is keyed by ad id (like adGroupNameEdits) so switching ads
                              // never surfaces a stale validation error from a different ad.
                              const adGroupNameError = selectedAdId != null ? adGroupNameErrors[selectedAdId] : undefined;
                              const setGroupNameError = (message: string | undefined) => {
                                if (selectedAdId == null) return;
                                setAdGroupNameErrors((prev) => {
                                  const next = { ...prev };
                                  if (message) next[selectedAdId] = message;
                                  else delete next[selectedAdId];
                                  return next;
                                });
                              };

                              const clearEdit = () => {
                                if (selectedAdId == null) return;
                                setAdGroupNameEdits((prev) => {
                                  const next = { ...prev };
                                  delete next[selectedAdId];
                                  return next;
                                });
                                setGroupNameError(undefined);
                              };

                              const handleSaveGroupName = async () => {
                                if (selectedAdId == null) return;
                                const trimmed = editedValue.trim();
                                if (!trimmed) {
                                  setGroupNameError('Required');
                                  return;
                                }
                                if (trimmed === currentGroupName) {
                                  clearEdit();
                                  return;
                                }
                                try {
                                  await renameAdGroupMutation.mutateAsync({
                                    campaignId: campaign.id,
                                    adId: selectedAdId,
                                    name: trimmed,
                                  });
                                  clearEdit();
                                } catch (e) {
                                  toast({
                                    description: e instanceof Error ? e.message : 'Failed to rename ad group',
                                    variant: 'destructive',
                                  });
                                }
                              };

                              return (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-slate-800 font-semibold">Google Ads Ad Group Name</h3>
                                    {adGroupNameError && (
                                      <span className="text-xs text-[#B91C1C]">{adGroupNameError}</span>
                                    )}
                                  </div>
                                  <Input
                                    value={editedValue}
                                    onChange={(e) => {
                                      if (selectedAdId == null) return;
                                      const value = e.target.value;
                                      setAdGroupNameEdits((prev) => ({ ...prev, [selectedAdId]: value }));
                                      if (adGroupNameError) setGroupNameError(undefined);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSaveGroupName();
                                      }
                                    }}
                                    maxLength={255}
                                    error={!!adGroupNameError}
                                    placeholder={`Gl-${selectedAd.url}-[uuid]`}
                                    className="!ring-inset text-gray-900 disabled:opacity-100 disabled:text-gray-900 placeholder:text-gray-400"
                                    style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                    disabled={isReadOnly || renameAdGroupMutation.isPending}
                                    readOnly={isReadOnly}
                                  />
                                  {isDirty && !isReadOnly && (
                                    <div className="flex items-center justify-between mt-2">
                                      <span className="text-xs text-gray-400">
                                        {isLive
                                          ? 'Saving updates the ad group name in Google Ads'
                                          : 'Applied to Google Ads when this campaign goes live'}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8"
                                          onClick={clearEdit}
                                          disabled={renameAdGroupMutation.isPending}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="h-8 bg-[#290BD9] hover:bg-[#2309C5]"
                                          onClick={handleSaveGroupName}
                                          disabled={renameAdGroupMutation.isPending}
                                        >
                                          {renameAdGroupMutation.isPending ? 'Saving...' : 'Save'}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {selectedAd.googleads_ad_group?.googleads_ad_group_id && (
                              <div>
                                <h3 className="text-slate-800 font-semibold mb-2">Google Ads Ad Group</h3>
                                <Input
                                  value={selectedAd.googleads_ad_group.googleads_ad_group_id}
                                  className="!ring-inset !ring-transparent text-gray-900 disabled:opacity-100 disabled:text-gray-900"
                                  style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                  disabled
                                  readOnly
                                />
                              </div>
                            )}

                            <div>
                              <h3 className="text-slate-800 font-semibold mb-2">Landing Page URL</h3>
                              {isReadOnly ? (
                                <div
                                  className="w-full min-h-10 rounded-md border border-input bg-transparent px-3 py-2 shadow-sm text-gray-900 break-all"
                                  style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' }}
                                >
                                  {selectedAd.url || ''}
                                </div>
                              ) : (
                                <>
                                  {/* #6480: onSelect writes the same adStates[selectedAdId].url shape the plain
                                      Input did, so the #5548 dirty-check + confirm-modal save path is unchanged. */}
                                  <BusinessLandingPageCombobox
                                    selectedUrl={selectedAdState?.url ?? null}
                                    onSelect={(entry) => {
                                      const v = entry?.url ?? '';
                                      setAdStates((prev) => ({
                                        ...prev,
                                        [selectedAdId!]: { ...prev[selectedAdId!], url: v },
                                      }));
                                    }}
                                    filterByDomain={selectedAd.url}
                                    placeholder="Select or paste a landing page URL..."
                                  />
                                  {/* #5548: surface the SEO/cost warning the moment the URL is edited; a
                                      red error blocks an invalid or off-domain URL. */}
                                  {selectedDirtyState?.isUrlDirty && (() => {
                                    const next = (selectedAdState?.url ?? '').trim();
                                    const invalid = !isValidUrl(next) || !isSameHost(next, selectedAd.url);
                                    return invalid ? (
                                      <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C] text-sm">
                                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                        <span>Enter a valid URL on the same domain as the current landing page.</span>
                                      </div>
                                    ) : (
                                      <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm">
                                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                        <span>{AD_URL_WARNING}</span>
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </div>

                          </div>

                          {/* Right column — Funnel Stages, then Intent */}
                          <div className="space-y-6">
                            {funnelStages.length > 0 && (
                              <div>
                                {/* "Angles" (#6381): a stage is one Google ad in the ad group,
                                    named by its GA label; native ads keep top/middle/bottom names. */}
                                <h3 className="text-slate-800 font-semibold mb-2">Angles</h3>
                                <div className="space-y-2">
                                  {funnelStages.map((fs: AdFunnelStage) => {
                                    const stageHeadlines = selectedAd.headlines?.filter((h: Headline) => h.ad_funnel_stage?.id === fs.id) || [];
                                    const stageDescriptions = selectedAd.descriptions?.filter((d: Description) => d.ad_funnel_stage?.id === fs.id) || [];
                                    const stageLabel = typeof fs.funnel === 'string' ? getFunnelStageLabel(fs.funnel) : '';
                                    const funnelValue = typeof fs.funnel === 'string' ? fs.funnel : '';
                                    return (
                                      <div
                                        key={fs.id}
                                        onClick={() => {
                                          setActiveFunnelTab(funnelValue as FunnelStage);
                                          handleTabChange('headlines');
                                        }}
                                        className="w-full flex flex-wrap gap-x-4 gap-y-1 min-h-10 items-center justify-between rounded-md border border-input shadow-sm px-3 py-2 transition-colors cursor-pointer text-left group/fs"
                                      >
                                        <span className="min-w-0 break-words text-sm text-gray-900 group-hover/fs:text-[#290BD9] group-hover/fs:underline transition-colors">{stageLabel}</span>
                                        <div className="flex shrink-0 items-center gap-4 text-xs text-gray-500 group-hover/fs:text-[#290BD9] transition-colors">
                                          <span>{stageHeadlines.length} headlines</span>
                                          <span>{stageDescriptions.length} descriptions</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {adIntent?.intent && (
                              <div>
                                <h3 className="text-slate-800 font-semibold mb-2">Intent</h3>
                                <textarea
                                  value={adIntent.intent.name ? (adIntent.intent.name.endsWith('.') ? adIntent.intent.name : `${adIntent.intent.name}.`) : ''}
                                  className="w-full text-gray-900 bg-transparent border border-slate-200 rounded-lg p-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#290BD9] focus:border-transparent disabled:opacity-100 disabled:text-gray-900"
                                  style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit', height: '220px' }}
                                  readOnly
                                />
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                  )
                })()}
                {/* === AD DETAIL TABS === */}
                {section !== 'overview' && (
                <div className='space-y-6'>
                  {/* Single loading spinner for rebuild or task processing */}
                  {shouldShowLoadingSkeleton && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Spinner size="xl" className="text-gray-400 mb-3" />
                      <p className="text-sm text-gray-500">Rebuilding ad content...</p>
                    </div>
                  )}

                  {selectedAd && selectedAdState && selectedDirtyState ? (
                    <>

                      {/* Content sections */}
                      {!shouldShowLoadingSkeleton && (
                        <>
                          {section === 'keywords' && (
                            <KeywordManager
                              keywords={selectedAdState.keywords}
                              setKeywords={(keywords) => updateAdState(selectedAdId!, 'keywords', keywords)}
                              adId={selectedAd.id}
                              alternativeKeywords={selectedAdState.alternativeKeywords}
                              setAlternativeKeywords={(alt) => updateAdState(selectedAdId!, 'alternativeKeywords', alt)}
                              customKeyword={selectedAdState.customKeyword}
                              setCustomKeyword={(custom) => updateAdState(selectedAdId!, 'customKeyword', custom)}
                              maxKeywordLength={15}
                              disabled={isReadOnly}
                              hideAddButton={isAdGenerating}
                              campaign={campaign || undefined}
                              googleadsAdGroupId={selectedAd?.googleads_ad_group?.googleads_ad_group_id}
                            />
                          )}

                          {section === 'negatives' && (
                            <NegativeKeywordsManager
                              campaign={campaign}
                              selectedAd={selectedAd}
                              disabled={isReadOnly}
                              hideAddButton={isAdGenerating}
                              onRefresh={refetchCampaign}
                              negativeKeywords={selectedAdState.negativeKeywords}
                              inheritedNegatives={adNegativeData?.inherited_negatives}
                              onRemove={(keyword) => {
                                if (!selectedAdId) return;
                                setAdStates((prev) => {
                                  const current = prev[selectedAdId]?.negativeKeywords || [];
                                  const updated = current.filter((nk: any) =>
                                    keyword.id ? nk.id !== keyword.id : (nk.keyword || nk.text) !== (keyword.keyword || keyword.text)
                                  );
                                  return { ...prev, [selectedAdId]: { ...prev[selectedAdId], negativeKeywords: updated } };
                                });
                              }}
                              onMatchTypeChange={(keyword, newMatchType) => {
                                if (!selectedAdId) return;
                                setAdStates((prev) => {
                                  const current = prev[selectedAdId]?.negativeKeywords || [];
                                  const updated = current.map((nk: any) => {
                                    const sameKw = keyword.id
                                      ? nk.id === keyword.id
                                      : (nk.keyword || nk.text) === (keyword.keyword || keyword.text) &&
                                        (nk.match_type || 'EXACT') === (keyword.match_type || 'EXACT');
                                    return sameKw ? { ...nk, match_type: newMatchType } : nk;
                                  });
                                  return { ...prev, [selectedAdId]: { ...prev[selectedAdId], negativeKeywords: updated } };
                                });
                              }}
                              onAdd={(newKeywords) => {
                                if (!selectedAdId) return;
                                setAdStates((prev) => {
                                  const current = prev[selectedAdId]?.negativeKeywords || [];
                                  const existing = new Set(current.map((nk: any) => `${(nk.keyword || nk.text || '').toLowerCase()}:${nk.match_type || 'EXACT'}`));
                                  const toAdd = newKeywords.filter((kw) => {
                                    const text = (kw.keyword || kw.text || '').toLowerCase();
                                    return text && !existing.has(`${text}:${kw.match_type || 'EXACT'}`);
                                  });
                                  return { ...prev, [selectedAdId]: { ...prev[selectedAdId], negativeKeywords: [...current, ...toAdd] } };
                                });
                              }}
                            />
                          )}

                          {section === 'labels' && selectedAdState && (
                            <AdLabelsSection
                              labels={selectedAdState.labels ?? []}
                              accountLabels={accountLabelsData?.labels ?? []}
                              isReadOnly={isReadOnly}
                              isGenerating={isAdGenerating}
                              onAdd={(label) => {
                                if (!selectedAdId) return;
                                setAdStates((prev) => {
                                  const cur = prev[selectedAdId]?.labels || [];
                                  if (cur.some((l: AdLabel) => l.name.toLowerCase() === label.name.toLowerCase())) return prev;
                                  return { ...prev, [selectedAdId]: { ...prev[selectedAdId], labels: [...cur, label] } };
                                });
                              }}
                              onRemove={(labelId) => {
                                if (!selectedAdId) return;
                                setAdStates((prev) => {
                                  const cur = prev[selectedAdId]?.labels || [];
                                  return { ...prev, [selectedAdId]: { ...prev[selectedAdId], labels: cur.filter((l: AdLabel) => (l.id || l.name) !== labelId) } };
                                });
                              }}
                            />
                          )}

                          {section === 'headlines' && (
                            <div>
                              <div className='flex items-center justify-between mb-4'>
                                <div>
                                  <div className='flex items-center gap-2'>
                                    <h2 className='text-xl font-semibold text-gray-900'>Headlines</h2>
                                    <StringBadge
                                      string={`${(() => {
                                        const headlines = activeRows(selectedAdState.headlines);
                                        if (isBrand) return headlines.length;
                                        const hasMetadata = headlines.length > 0 && headlines.some((h: Headline) => h.ad_funnel_stage);
                                        return hasMetadata
                                          ? headlines.filter((h: Headline) => h.ad_funnel_stage?.funnel === activeFunnelTab).length
                                          : headlines.length;
                                      })()}/15`}
                                      size='sm'
                                    />
                                  </div>
                                  <p className='text-sm text-gray-500 mt-1'>The bold titles people see first when your ad shows up.</p>
                                </div>
                                {!isAdGenerating && (
                                <div className='flex items-center gap-2'>
                                  <Button
                                    variant='outline'
                                    className='text-gray-900'
                                    onClick={() => setShowHeadlineAddModal(true)}
                                    disabled={(() => {
                                      const headlines = activeRows(selectedAdState.headlines);
                                      const hasMetadata = headlines.length > 0 && headlines.some((h: Headline) => h.ad_funnel_stage);
                                      const currentCount = hasMetadata
                                        ? headlines.filter((h: Headline) => h.ad_funnel_stage?.funnel === activeFunnelTab).length
                                        : headlines.length;
                                      return currentCount >= 15 || isReadOnly;
                                    })()}
                                  >
                                    <Plus className='h-4 w-4 mr-2' />
                                    Add
                                  </Button>
                                  <Button
                                    variant='outline'
                                    className='text-gray-900'
                                    onClick={() => handleGenerateHeadlines(activeFunnelTab)}
                                    disabled={(() => {
                                      const headlines = activeRows(selectedAdState.headlines);
                                      const hasMetadata = headlines.length > 0 && headlines.some((h: Headline) => h.ad_funnel_stage);
                                      const currentCount = hasMetadata
                                        ? headlines.filter((h: Headline) => h.ad_funnel_stage?.funnel === activeFunnelTab).length
                                        : headlines.length;
                                      return currentCount >= 15 || selectedDirtyState.isHeadlinesDirty || isGeneratingHeadlines;
                                    })()}
                                  >
                                    {isGeneratingHeadlines ? (
                                      <Spinner size="sm" className="mr-2" />
                                    ) : (
                                      <Wand2 className='h-4 w-4 mr-2' />
                                    )}
                                    {isGeneratingHeadlines ? 'Generating...' : 'Generate'}
                                  </Button>
                                </div>
                                )}
                              </div>
                              <HeadlineManager
                                title={selectedAd.headlines?.slice(0, 3).map((h: Headline) => h?.text).join(' | ') || 'New Headlines'}
                                description={selectedAd.descriptions?.[0]?.text || 'Add description'}
                                headlines={selectedAdState.headlines}
                                setHeadlines={(headlines) => { setDuplicateHeadlineTexts(new Set()); updateAdState(selectedAdId!, 'headlines', headlines); }}
                                maxHeadlines={15}
                                adState={selectedAd.state}
                                disabled={isReadOnly}
                                campaign={campaign || undefined}
                                activeFunnel={activeFunnelTab}
                                onFunnelChange={setActiveFunnelTab}
                                campaignId={campaign.id}
                                adId={selectedAdId!}
                                highlightedIds={newlyGeneratedHeadlineIds}
                                metricsData={headlineMetricsData}
                                metricsLoading={headlineMetricsLoading}
                                funnelStages={selectedAd.ad_funnel_stage}
                                isGeneratingInBackground={(campaignTaskTracking?.headline_generation?.in_progress ?? 0) > 0}
                                duplicateTexts={duplicateHeadlineTexts}
                                showAddModal={showHeadlineAddModal}
                                onAddModalClose={() => setShowHeadlineAddModal(false)}
                              />
                            </div>
                          )}

                          {section === 'descriptions' && (
                            <div>
                              <div className='flex items-center justify-between mb-4'>
                                <div>
                                  <div className='flex items-center gap-2'>
                                    <h2 className='text-xl font-semibold text-gray-900'>Descriptions</h2>
                                    <StringBadge
                                      string={`${(() => {
                                        const descriptions = activeRows(selectedAdState.descriptions);
                                        if (isBrand) return descriptions.length;
                                        const hasMetadata = descriptions.some((d: Description) => d.ad_funnel_stage);
                                        return hasMetadata
                                          ? descriptions.filter((d: Description) => d.ad_funnel_stage?.funnel === activeFunnelTab).length
                                          : descriptions.length;
                                      })()}/4`}
                                      size='sm'
                                    />
                                  </div>
                                  <p className='text-sm text-gray-500 mt-1'>The text below your headlines that tells people what you offer.</p>
                                </div>
                                {!isAdGenerating && (
                                <div className='flex items-center gap-2'>
                                  <Button
                                    variant='outline'
                                    className='text-gray-900'
                                    onClick={() => setShowDescriptionAddModal(true)}
                                    disabled={(() => {
                                      const descriptions = activeRows(selectedAdState.descriptions);
                                      const hasMetadata = descriptions.some((d: Description) => d.ad_funnel_stage);
                                      const currentCount = hasMetadata
                                        ? descriptions.filter((d: Description) => d.ad_funnel_stage?.funnel === activeFunnelTab).length
                                        : descriptions.length;
                                      return currentCount >= 4 || isReadOnly;
                                    })()}
                                  >
                                    <Plus className='h-4 w-4 mr-2' />
                                    Add
                                  </Button>
                                  <Button
                                    variant='outline'
                                    className='text-gray-900'
                                    onClick={() => handleGenerateDescriptions(activeFunnelTab)}
                                    disabled={(() => {
                                      const descriptions = activeRows(selectedAdState.descriptions);
                                      const hasMetadata = descriptions.some((d: Description) => d.ad_funnel_stage);
                                      const currentCount = hasMetadata
                                        ? descriptions.filter((d: Description) => d.ad_funnel_stage?.funnel === activeFunnelTab).length
                                        : descriptions.length;
                                      return currentCount >= 4 || selectedDirtyState.isDescriptionsDirty || isGeneratingDescriptions;
                                    })()}
                                  >
                                    {isGeneratingDescriptions ? (
                                      <Spinner size="sm" className="mr-2" />
                                    ) : (
                                      <Wand2 className='h-4 w-4 mr-2' />
                                    )}
                                    {isGeneratingDescriptions ? 'Generating...' : 'Generate'}
                                  </Button>
                                </div>
                                )}
                              </div>
                              <DescriptionManagement
                                descriptions={selectedAdState.descriptions}
                                setDescriptions={(descriptions) => updateAdState(selectedAdId!, 'descriptions', descriptions)}
                                disabled={isReadOnly}
                                campaign={campaign || undefined}
                                activeTab={activeFunnelTab}
                                onTabChange={setActiveFunnelTab}
                                metricsData={descriptionMetricsData}
                                metricsLoading={descriptionMetricsLoading}
                                funnelStages={selectedAd.ad_funnel_stage}
                                showAddModal={showDescriptionAddModal}
                                onAddModalClose={() => setShowDescriptionAddModal(false)}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : null}
                </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ConformationModal
        isOpen={showDeleteAdConfirm}
        onClose={() => setShowDeleteAdConfirm(false)}
        onConfirm={() => deleteAdMutation.mutate()}
        title="Delete Ad"
        description="Are you sure you want to delete this ad? This action cannot be undone."
        confirmText="Delete Ad"
        cancelText="Cancel"
        isLoading={deleteAdMutation.isPending}
        loadingText="Deleting..."
      />
      {/* #5548: confirm a landing page URL change before saving (SEO / cost impact). */}
      <ConformationModal
        isOpen={urlWarningAdId != null}
        onClose={() => setUrlWarningAdId(null)}
        onConfirm={confirmUrlSave}
        title="Change landing page URL?"
        description={AD_URL_WARNING}
        confirmText="Change URL"
        cancelText="Cancel"
        variant="warning"
      />
      {/* Add Ad Modal — same component as the campaign detail page */}
      {campaign.website_url && (
        <AdCreateModal
          isOpen={isCreateAdModalOpen}
          onClose={() => setIsCreateAdModalOpen(false)}
          campaignUrl={campaign.website_url}
          setPrefixedUrl={setPrefixedUrl}
          handleCreateAd={handleCreateAd}
          isCompetitorCampaign={isCompetitorCampaign}
          competitors={availableCompetitors ?? []}
          isLoadingCompetitors={isLoadingCompetitors}
          setSelectedCompetitorId={setSelectedCompetitorId}
        />
      )}
      {/* Ad Preview Modal */}
      {selectedAd && (
        <AdPreviewModal
          ad={selectedAd}
          campaign={campaign}
          open={showAdPreviewModal}
          onOpenChange={setShowAdPreviewModal}
        />
      )}
    </div>
  )
}
